import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Contact,
  FileText,
  Loader2,
  Megaphone,
  MessageSquare,
  Pencil,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Search,
  Send,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DEFAULT_LEAD_SOURCES,
  DEFAULT_LEAD_STATUSES,
  formatCrmDateTime,
  formatCrmDuration,
  formatRelativeTime,
  getAuthorInitials,
  getCustomerInitials,
  getLeadSourceMeta,
  getLeadStatusBadgeClass,
  LEAD_TONE_DOT,
  LEAD_TONE_RING,
} from "@/utils/crmHelpers";
import { getLast7DaysStr, getTodayStr } from "@/utils/helpers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const CallStatusBadge = ({ durum, durum_aciklama, isDark }) => {
  const label = durum_aciklama === "cevaplanan"
    ? "Cevaplandı"
    : durum_aciklama === "mesgul"
      ? "Meşgul"
      : "Cevapsız";
  const cls = durum === "1"
    ? (isDark ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/25" : "bg-emerald-100 text-emerald-700 ring-emerald-200")
    : durum === "3"
      ? (isDark ? "bg-rose-500/15 text-rose-300 ring-rose-500/25" : "bg-rose-100 text-rose-700 ring-rose-200")
      : (isDark ? "bg-amber-500/15 text-amber-300 ring-amber-500/25" : "bg-amber-100 text-amber-800 ring-amber-200");
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${cls}`}>{label}</span>;
};

const NoteCard = ({
  note,
  isDark,
  inputClass,
  mutedClass,
  textClass,
  editingNote,
  editNoteText,
  setEditNoteText,
  setEditingNote,
  onUpdate,
  onDelete,
  noteUpdating,
}) => {
  const isEditing = editingNote === note.id;
  const authorInitials = getAuthorInitials(note.created_by_name);

  return (
    <div className="relative pl-8 pb-5 last:pb-0 group">
      <div className={`absolute left-[11px] top-8 bottom-0 w-px ${isDark ? "bg-slate-800" : "bg-gray-200"} group-last:hidden`} />
      <div className={`absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ring-2 ${
        isDark ? "bg-violet-500/20 text-violet-300 ring-slate-950" : "bg-violet-100 text-violet-700 ring-white"
      }`}>
        {authorInitials}
      </div>

      <div className={`rounded-xl border transition-shadow ${
        isEditing
          ? (isDark ? "border-violet-500/40 bg-slate-900/60 shadow-lg shadow-violet-500/5" : "border-violet-200 bg-white shadow-md")
          : (isDark ? "border-slate-800/80 bg-slate-900/40 hover:border-slate-700" : "border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm")
      }`}>
        <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className={`text-xs font-semibold ${textClass}`}>
                {note.created_by_name || "Sistem"}
              </span>
              <span className={`text-[10px] ${mutedClass}`} title={formatCrmDateTime(note.created_at)}>
                {formatRelativeTime(note.created_at)}
              </span>
            </div>
          </div>
          {!isEditing && (
            <div className="flex gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="ghost"
                className={`h-7 w-7 p-0 ${isDark ? "hover:bg-slate-800" : ""}`}
                onClick={() => { setEditingNote(note.id); setEditNoteText(note.note_text); }}
              >
                <Pencil className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-rose-500 hover:text-rose-600"
                onClick={() => onDelete(note.id)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>

        <div className="px-4 pb-3">
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editNoteText}
                onChange={(e) => setEditNoteText(e.target.value)}
                rows={3}
                className={`text-sm resize-none ${inputClass}`}
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setEditingNote(null)} disabled={noteUpdating}>
                  <X className="w-3.5 h-3.5 mr-1" />İptal
                </Button>
                <Button
                  size="sm"
                  onClick={() => onUpdate(note.id)}
                  disabled={!editNoteText.trim() || noteUpdating}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  {noteUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Kaydet"}
                </Button>
              </div>
            </div>
          ) : (
            <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isDark ? "text-slate-200" : "text-gray-800"}`}>
              {note.note_text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomerDetailView = ({
  phone,
  isDark,
  leadStatuses = DEFAULT_LEAD_STATUSES,
  leadSources = DEFAULT_LEAD_SOURCES,
  onLeadStatusesChange,
  onLeadSourcesChange,
  onProfileChange,
  onViewTranscript,
  onBack,
  canSeeRecordings,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({});
  const [calls, setCalls] = useState([]);
  const [noteHistory, setNoteHistory] = useState([]);
  const [leadSourceHistory, setLeadSourceHistory] = useState([]);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [transcriptData, setTranscriptData] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [callSearch, setCallSearch] = useState("");
  const [callStartDate, setCallStartDate] = useState("");
  const [callEndDate, setCallEndDate] = useState("");
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteUpdating, setNoteUpdating] = useState(false);
  const [activityTab, setActivityTab] = useState("calls");

  const shellClass = isDark
    ? "rounded-2xl border border-slate-800/80 bg-slate-950/60 shadow-xl shadow-black/20"
    : "rounded-2xl border border-gray-200/90 bg-white shadow-sm";
  const inputClass = isDark ? "bg-slate-900/80 border-slate-700 text-white" : "bg-white border-gray-300";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";
  const labelClass = isDark ? "text-slate-400" : "text-gray-600";
  const textClass = isDark ? "text-white" : "text-gray-900";

  const loadCustomer = useCallback(async () => {
    if (!phone) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers`, { params: { phone, call_limit: 100 }, timeout: 30000 });
      setProfile(res.data.profile || {});
      const loadedCalls = res.data.calls || [];
      setCalls(loadedCalls);
      setNoteHistory(res.data.note_history || []);
      setLeadSourceHistory(res.data.lead_source_history || []);
      if (res.data.lead_statuses?.length) onLeadStatusesChange?.(res.data.lead_statuses);
      if (res.data.lead_sources?.length) onLeadSourcesChange?.(res.data.lead_sources);
      onProfileChange?.(phone, res.data.profile);
      if (loadedCalls.length) {
        setSelectedCallId((prev) => prev || loadedCalls[0].id);
        const dates = loadedCalls.map((c) => (c.tarih || "").split(" ")[0]).filter(Boolean).sort();
        if (dates.length) {
          setCallStartDate(dates[0]);
          setCallEndDate(dates[dates.length - 1]);
        }
      } else {
        setCallEndDate(getTodayStr());
        setCallStartDate(getLast7DaysStr());
      }
    } catch {
      toast.error("Müşteri kartı yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [phone, onProfileChange, onLeadStatusesChange, onLeadSourcesChange]);

  useEffect(() => {
    setSelectedCallId(null);
    setTranscriptData(null);
    setActivityTab("calls");
    loadCustomer();
  }, [phone, loadCustomer]);

  const selectedCall = calls.find((c) => c.id === selectedCallId) || null;

  useEffect(() => {
    if (!selectedCallId || !canSeeRecordings) {
      setTranscriptData(null);
      return undefined;
    }
    let cancelled = false;
    setTranscriptLoading(true);
    axios.get(`${API}/transcripts/${selectedCallId}`)
      .then((res) => { if (!cancelled) setTranscriptData(res.data); })
      .catch(() => { if (!cancelled) setTranscriptData(null); })
      .finally(() => { if (!cancelled) setTranscriptLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCallId, canSeeRecordings]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/customers`, profile, { params: { phone } });
      setProfile(res.data.profile || profile);
      if (res.data.note_history) setNoteHistory(res.data.note_history);
      if (res.data.lead_source_history) setLeadSourceHistory(res.data.lead_source_history);
      onProfileChange?.(phone, res.data.profile);
      toast.success("Kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || noteSubmitting) return;
    setNoteSubmitting(true);
    try {
      const res = await axios.post(`${API}/customer-notes`, { phone, note_text: newNote.trim() });
      const note = res.data?.note;
      if (note) {
        setNoteHistory((prev) => [note, ...prev]);
      }
      setNewNote("");
      toast.success("Not eklendi");
    } catch {
      toast.error("Not eklenemedi");
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteText.trim() || noteUpdating) return;
    setNoteUpdating(true);
    try {
      const res = await axios.put(`${API}/customer-notes?id=${noteId}`, { note_text: editNoteText.trim() });
      const updated = res.data?.note;
      if (updated) {
        setNoteHistory((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      }
      setEditingNote(null);
      setEditNoteText("");
      toast.success("Not güncellendi");
    } catch {
      toast.error("Güncellenemedi");
    } finally {
      setNoteUpdating(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Not silinsin mi?")) return;
    try {
      await axios.delete(`${API}/customer-notes?id=${noteId}`);
      setNoteHistory((prev) => prev.filter((n) => n.id !== noteId));
      if (editingNote === noteId) setEditingNote(null);
      toast.success("Not silindi");
    } catch {
      toast.error("Silinemedi");
    }
  };

  const handleNoteKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleAddNote();
    }
  };

  const validLeadValue = leadStatuses.some((s) => s.value === profile.lead_durumu)
    ? profile.lead_durumu
    : (leadStatuses[0]?.value || "yeni");

  const filteredCalls = useMemo(() => {
    const q = callSearch.trim().toLowerCase();
    return calls.filter((c) => {
      const datePart = (c.tarih || "").split(" ")[0];
      if (callStartDate && datePart && datePart < callStartDate) return false;
      if (callEndDate && datePart && datePart > callEndDate) return false;
      if (!q) return true;
      return (
        (c.tarih || "").toLowerCase().includes(q) ||
        (c.agent_name || "").toLowerCase().includes(q) ||
        (c.summary_text || "").toLowerCase().includes(q) ||
        String(c.arayan_numara || "").includes(q) ||
        String(c.aranan_numara || "").includes(q)
      );
    });
  }, [calls, callSearch, callStartDate, callEndDate]);

  const applyCallDatePreset = (preset) => {
    const today = getTodayStr();
    if (preset === "all") {
      setCallStartDate("");
      setCallEndDate("");
      return;
    }
    if (preset === "7") {
      setCallStartDate(getLast7DaysStr());
      setCallEndDate(today);
      return;
    }
    if (preset === "30") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      setCallStartDate(d.toISOString().split("T")[0]);
      setCallEndDate(today);
    }
  };

  const lastCallDate = calls[0]?.tarih ? formatCrmDateTime(calls[0].tarih) : "-";
  const initials = getCustomerInitials(profile.ad_soyad, phone);

  const tabBtnClass = (active) => (
    active
      ? (isDark ? "bg-violet-600/25 text-violet-200 border-violet-500/40 shadow-sm" : "bg-violet-100 text-violet-800 border-violet-200")
      : (isDark ? "text-slate-400 border-transparent hover:text-slate-200 hover:bg-slate-800/60" : "text-gray-500 border-transparent hover:text-gray-800 hover:bg-gray-100")
  );

  if (!phone) {
    return (
      <div className={`${shellClass} p-10 text-center`}>
        <Contact className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? "text-violet-400" : "text-violet-600"}`} />
        <p className={labelClass}>Müşteri seçilmedi</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>Listeye Dön</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`${shellClass} flex flex-col items-center justify-center py-24 gap-3`}>
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        <p className={`text-sm ${mutedClass}`}>Müşteri kartı yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={isDark ? "text-slate-400 hover:text-white hover:bg-slate-800" : "text-gray-600"}
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Müşteriler
        </Button>
        <span className={mutedClass}>/</span>
        <span className={`text-sm font-medium truncate ${textClass}`}>
          {profile.ad_soyad?.trim() || phone}
        </span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(280px,340px)_1fr] gap-4 items-start">
        {/* Sol — profil */}
        <div className="space-y-4 xl:sticky xl:top-4">
          <div className={`${shellClass} overflow-hidden`}>
            <div className={`h-20 ${isDark ? "bg-gradient-to-r from-violet-600/30 via-fuchsia-600/20 to-slate-900" : "bg-gradient-to-r from-violet-500/15 via-fuchsia-400/10 to-white"}`} />
            <div className="px-5 pb-5 -mt-10">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-lg font-bold ring-4 ${
                isDark ? "bg-slate-900 text-violet-300 ring-slate-950" : "bg-white text-violet-700 ring-white shadow-md"
              }`}>
                {initials}
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <Label className={`text-xs ${labelClass}`}>Ad Soyad</Label>
                  <Input
                    value={profile.ad_soyad || ""}
                    onChange={(e) => setProfile((p) => ({ ...p, ad_soyad: e.target.value }))}
                    className={`mt-1 ${inputClass}`}
                    placeholder="Müşteri adı"
                  />
                </div>
                <div>
                  <Label className={`text-xs ${labelClass}`}>Telefon</Label>
                  <p className={`mt-1 font-mono text-sm ${isDark ? "text-slate-200" : "text-gray-800"}`}>{phone}</p>
                </div>

                <div>
                  <Label className={`text-xs ${labelClass}`}>Lead Durumu</Label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {leadStatuses.map((s) => {
                      const active = validLeadValue === s.value;
                      const tone = s.tone || "violet";
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => setProfile((p) => ({ ...p, lead_durumu: s.value }))}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                            active
                              ? `${getLeadStatusBadgeClass(s.value, isDark, leadStatuses)} ring-2 ${LEAD_TONE_RING[tone] || LEAD_TONE_RING.violet}`
                              : (isDark ? "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200" : "border-gray-200 text-gray-600 hover:border-gray-300")
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${LEAD_TONE_DOT[tone] || LEAD_TONE_DOT.violet}`} />
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className={`text-xs ${labelClass}`}>Lead Kaynağı</Label>
                  <Select value={profile.lead_kaynagi || ""} onValueChange={(v) => setProfile((p) => ({ ...p, lead_kaynagi: v }))}>
                    <SelectTrigger className={`mt-1 ${inputClass}`}><SelectValue placeholder="Kaynak seçin" /></SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                      {leadSources.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {profile.lead_kaynagi && (
                    <p className={`text-xs mt-1.5 flex items-center gap-1 ${mutedClass}`}>
                      <Megaphone className="w-3 h-3" />
                      {profile.lead_kaynagi_label || getLeadSourceMeta(profile.lead_kaynagi, leadSources)?.label}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                >
                  {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                </Button>
              </div>

              <div className={`grid grid-cols-3 gap-2 mt-4 pt-4 border-t ${isDark ? "border-slate-800" : "border-gray-100"}`}>
                <button
                  type="button"
                  onClick={() => setActivityTab("calls")}
                  className={`rounded-xl px-2 py-2 text-left transition-colors ${isDark ? "bg-slate-900/70 hover:bg-slate-800/70" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <p className={`text-[10px] uppercase tracking-wide ${mutedClass}`}>Görüşme</p>
                  <p className={`text-lg font-semibold ${textClass}`}>{calls.length}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setActivityTab("notes")}
                  className={`rounded-xl px-2 py-2 text-left transition-colors ${isDark ? "bg-slate-900/70 hover:bg-slate-800/70" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <p className={`text-[10px] uppercase tracking-wide ${mutedClass}`}>Not</p>
                  <p className={`text-lg font-semibold ${textClass}`}>{noteHistory.length}</p>
                </button>
                <div className={`rounded-xl px-2 py-2 ${isDark ? "bg-slate-900/70" : "bg-gray-50"}`}>
                  <p className={`text-[10px] uppercase tracking-wide ${mutedClass}`}>Son temas</p>
                  <p className={`text-[10px] font-medium mt-0.5 leading-tight ${isDark ? "text-slate-300" : "text-gray-700"}`}>{lastCallDate}</p>
                </div>
              </div>
            </div>
          </div>

          {leadSourceHistory.length > 0 && (
            <div className={`${shellClass} p-4 space-y-2`}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${labelClass}`}>Kaynak geçmişi</p>
              {leadSourceHistory.slice(0, 4).map((item) => (
                <div key={item.id} className={`text-xs rounded-lg px-2.5 py-2 ${isDark ? "bg-slate-900/50" : "bg-gray-50"}`}>
                  <span className={mutedClass}>{formatCrmDateTime(item.created_at)}</span>
                  <p className={`mt-0.5 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                    {item.previous_label ? `${item.previous_label} → ` : ""}
                    <span className="font-medium">{item.lead_kaynagi_label || item.lead_kaynagi}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sağ — görüşmeler & notlar */}
        <div className={`${shellClass} overflow-hidden flex flex-col min-h-[520px]`}>
          <div className={`px-5 pt-4 pb-0 border-b ${isDark ? "border-slate-800" : "border-gray-100"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <h3 className={`font-semibold ${textClass}`}>Müşteri Geçmişi</h3>
                <p className={`text-xs mt-0.5 ${mutedClass}`}>Görüşmeler ve notlar — ihtiyaç duyduğunuzda buradan bakın</p>
              </div>
            </div>

            <div className={`flex gap-1 p-1 rounded-xl mb-4 ${isDark ? "bg-slate-900/60" : "bg-gray-100"}`}>
              <button
                type="button"
                onClick={() => setActivityTab("calls")}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${tabBtnClass(activityTab === "calls")}`}
              >
                <Phone className="w-4 h-4" />
                Görüşmeler
                <Badge variant="secondary" className={`text-[10px] h-5 ${isDark ? "bg-slate-800" : ""}`}>{filteredCalls.length}</Badge>
              </button>
              <button
                type="button"
                onClick={() => setActivityTab("notes")}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${tabBtnClass(activityTab === "notes")}`}
              >
                <StickyNote className="w-4 h-4" />
                Notlar
                <Badge variant="secondary" className={`text-[10px] h-5 ${isDark ? "bg-slate-800" : ""}`}>{noteHistory.length}</Badge>
              </button>
            </div>
          </div>

          {activityTab === "calls" ? (
            <>
              <div className={`px-5 py-3 border-b ${isDark ? "border-slate-800" : "border-gray-100"}`}>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {[
                    { id: "7", label: "7 Gün" },
                    { id: "30", label: "30 Gün" },
                    { id: "all", label: "Tümü" },
                  ].map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyCallDatePreset(p.id)}
                      className={`h-7 px-2.5 rounded-lg text-xs font-medium transition-colors ${
                        isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <Input type="date" value={callStartDate} onChange={(e) => setCallStartDate(e.target.value)} className={`h-8 text-xs w-36 ${inputClass}`} />
                  <Input type="date" value={callEndDate} onChange={(e) => setCallEndDate(e.target.value)} className={`h-8 text-xs w-36 ${inputClass}`} />
                  <div className="relative flex-1 min-w-[160px]">
                    <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${mutedClass}`} />
                    <Input
                      value={callSearch}
                      onChange={(e) => setCallSearch(e.target.value)}
                      placeholder="Agent, özet, numara..."
                      className={`h-8 text-xs pl-8 ${inputClass}`}
                    />
                  </div>
                </div>
              </div>

              <div className="divide-y flex-1 overflow-y-auto max-h-[calc(100vh-320px)]">
                {filteredCalls.length === 0 ? (
                  <div className={`py-16 text-center text-sm ${mutedClass}`}>
                    <Phone className="w-10 h-10 mx-auto mb-2 opacity-20" />
                    Görüşme kaydı bulunamadı
                  </div>
                ) : filteredCalls.map((call) => {
                  const selected = selectedCallId === call.id;
                  const incoming = call.yon === 1;
                  const outgoing = call.yon === 2;
                  return (
                    <div key={call.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedCallId(selected ? null : call.id)}
                        className={`w-full text-left px-5 py-4 transition-colors border-l-2 ${
                          selected
                            ? (isDark ? "bg-violet-500/10 border-violet-400" : "bg-violet-50 border-violet-500")
                            : (isDark ? "border-transparent hover:bg-slate-900/50" : "border-transparent hover:bg-gray-50")
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                              incoming
                                ? (isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700")
                                : outgoing
                                  ? (isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-700")
                                  : (isDark ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500")
                            }`}>
                              {incoming ? <PhoneIncoming className="w-4 h-4" /> : outgoing ? <PhoneOutgoing className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm font-medium ${textClass}`}>{call.agent_name || "Agent yok"}</p>
                              <p className={`text-xs font-mono mt-0.5 ${mutedClass}`}>{call.tarih}</p>
                              {call.summary_text && (
                                <p className={`text-xs mt-1.5 line-clamp-2 ${isDark ? "text-slate-400" : "text-gray-600"}`}>{call.summary_text}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 shrink-0">
                            <span className={`text-xs font-mono ${mutedClass}`}>{formatCrmDuration(call.konusma_suresi)}</span>
                            <CallStatusBadge durum={call.durum} durum_aciklama={call.durum_aciklama} isDark={isDark} />
                          </div>
                        </div>
                      </button>

                      {selected && selectedCall && (
                        <div className={`px-5 pb-4 ${isDark ? "bg-slate-900/30" : "bg-violet-50/30"}`}>
                          <div className={`rounded-xl border p-4 space-y-3 ${isDark ? "border-slate-800 bg-slate-950/50" : "border-violet-100 bg-white"}`}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                              <div><p className={`text-[10px] uppercase ${labelClass}`}>Arayan</p><p className="font-mono text-xs mt-0.5">{selectedCall.arayan_numara || "-"}</p></div>
                              <div><p className={`text-[10px] uppercase ${labelClass}`}>Aranan</p><p className="font-mono text-xs mt-0.5">{selectedCall.aranan_numara || "-"}</p></div>
                              <div><p className={`text-[10px] uppercase ${labelClass}`}>Süre</p><p className="font-mono text-xs mt-0.5">{formatCrmDuration(selectedCall.konusma_suresi)}</p></div>
                              <div><p className={`text-[10px] uppercase ${labelClass}`}>Durum</p><div className="mt-0.5"><CallStatusBadge durum={selectedCall.durum} durum_aciklama={selectedCall.durum_aciklama} isDark={isDark} /></div></div>
                            </div>
                            {selectedCall.summary_text && (
                              <div>
                                <p className={`text-[10px] uppercase mb-1 ${labelClass}`}>Özet</p>
                                <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-gray-700"}`}>{selectedCall.summary_text}</p>
                              </div>
                            )}
                            {canSeeRecordings && (
                              <div className="flex flex-wrap gap-2 pt-1">
                                {selectedCall.has_transcript || selectedCall.transkript === 1 || selectedCall.recording ? (
                                  <Button size="sm" onClick={() => onViewTranscript?.(selectedCall.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">
                                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                                    Transkript
                                  </Button>
                                ) : null}
                                {transcriptLoading && <Loader2 className="w-4 h-4 animate-spin text-violet-500" />}
                                {!transcriptLoading && transcriptData?.transcript_text && (
                                  <div className={`w-full rounded-lg border p-3 text-xs max-h-32 overflow-y-auto whitespace-pre-wrap ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
                                    {(transcriptData.transcript_text || "").slice(0, 800)}{(transcriptData.transcript_text || "").length > 800 ? "..." : ""}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              <div className={`px-5 py-4 border-b ${isDark ? "border-slate-800 bg-slate-900/20" : "border-gray-100 bg-violet-50/30"}`}>
                <div className={`rounded-xl border p-3 space-y-2 ${
                  isDark ? "border-violet-500/20 bg-slate-950/40" : "border-violet-100 bg-white shadow-sm"
                }`}>
                  <div className="flex items-center gap-2">
                    <MessageSquare className={`w-4 h-4 ${isDark ? "text-violet-400" : "text-violet-600"}`} />
                    <span className={`text-xs font-medium ${labelClass}`}>Yeni not</span>
                    <span className={`text-[10px] ml-auto ${mutedClass}`}>Ctrl+Enter ile gönder</span>
                  </div>
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={handleNoteKeyDown}
                    rows={3}
                    placeholder="Müşteriyle ilgili notunuzu yazın — görüşme detayları, takip bilgisi, önemli hatırlatmalar..."
                    className={`text-sm resize-none border-0 shadow-none focus-visible:ring-0 px-0 ${isDark ? "bg-transparent text-white placeholder:text-slate-500" : "bg-transparent placeholder:text-gray-400"}`}
                  />
                  <div className={`flex items-center justify-between pt-1 border-t border-dashed ${isDark ? "border-slate-800" : "border-gray-100"}`}>
                    <span className={`text-[10px] ${mutedClass}`}>{newNote.length > 0 ? `${newNote.length} karakter` : ""}</span>
                    <Button
                      size="sm"
                      onClick={handleAddNote}
                      disabled={!newNote.trim() || noteSubmitting}
                      className="bg-violet-600 hover:bg-violet-700 text-white h-8 px-4"
                    >
                      {noteSubmitting ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                          Kaydet
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 max-h-[calc(100vh-380px)]">
                {noteHistory.length === 0 ? (
                  <div className={`py-16 text-center ${mutedClass}`}>
                    <StickyNote className="w-10 h-10 mx-auto mb-2 opacity-20" />
                    <p className="text-sm">Henüz not yok</p>
                    <p className="text-xs mt-1">İlk notunuzu yukarıdan ekleyin</p>
                  </div>
                ) : (
                  <div className="relative">
                    {noteHistory.map((note) => (
                      <NoteCard
                        key={note.id}
                        note={note}
                        isDark={isDark}
                        inputClass={inputClass}
                        mutedClass={mutedClass}
                        textClass={textClass}
                        editingNote={editingNote}
                        editNoteText={editNoteText}
                        setEditNoteText={setEditNoteText}
                        setEditingNote={setEditingNote}
                        onUpdate={handleUpdateNote}
                        onDelete={handleDeleteNote}
                        noteUpdating={noteUpdating}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailView;
